<template>
  <CCard>
    <CCardHeader class="d-flex justify-content-between align-items-center">
      <strong>Blacklist agencija i oglašivača</strong>
      <span class="badge badge-dark">Odabrano: {{ selectedPublishers.length }}</span>
    </CCardHeader>
    <CCardBody>
      <p class="text-muted">
        Blacklistane agencije ne prikazuju se među oglasima i ne ulaze u izračun kvartovskih cijena. Oglasi ostaju spremljeni u bazi.
      </p>

      <CRow>
        <CCol lg="6">
          <CCard>
            <CCardHeader class="d-flex justify-content-between align-items-center">
              <strong>Dostupne agencije</strong>
              <span class="badge badge-secondary">{{ availablePublishers.length }}</span>
            </CCardHeader>
            <CCardBody>
              <input v-model="searchText" class="form-control mb-3" type="search" placeholder="Pretraži agencije..." />
              <div class="input-group mb-3">
                <input
                  v-model="manualPublisherName"
                  class="form-control"
                  type="text"
                  placeholder="Ili upiši naziv ručno"
                  @keyup.enter="addManualPublisher"
                />
                <div class="input-group-append">
                  <CButton color="secondary" :disabled="!manualPublisherName.trim()" @click="addManualPublisher">Dodaj</CButton>
                </div>
              </div>

              <div class="publisher-list">
                <div v-for="publisher in availablePublishers" :key="publisher.normalizedName" class="publisher-row">
                  <div class="publisher-name" :title="publisher.name">
                    {{ publisher.name }}
                    <span class="badge badge-light ml-1">{{ publisher.activeCount }}</span>
                  </div>
                  <CButton color="primary" size="sm" variant="outline" @click="addPublisher(publisher)">Dodaj</CButton>
                </div>
                <div v-if="!isLoading && availablePublishers.length === 0" class="empty-state">
                  Nema agencija koje odgovaraju pretrazi.
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg="6">
          <CCard>
            <CCardHeader class="d-flex justify-content-between align-items-center">
              <strong>Blacklistane agencije</strong>
              <span class="badge badge-danger">{{ selectedPublishers.length }}</span>
            </CCardHeader>
            <CCardBody>
              <div class="publisher-list">
                <div v-for="publisher in selectedPublishersSorted" :key="publisher.normalizedName" class="publisher-row">
                  <div class="publisher-name" :title="publisher.name">
                    {{ publisher.name }}
                    <span v-if="publisher.activeCount" class="badge badge-light ml-1">{{ publisher.activeCount }}</span>
                  </div>
                  <CButton color="danger" size="sm" variant="outline" @click="removePublisher(publisher)">Ukloni</CButton>
                </div>
                <div v-if="!isLoading && selectedPublishers.length === 0" class="empty-state">Nijedna agencija nije blacklistana.</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <div class="d-flex align-items-center mt-2">
        <CButton color="primary" :disabled="isLoading || isSaving || !hasChanges" @click="save">
          {{ isSaving ? "Spremanje..." : "Spremi promjene" }}
        </CButton>
        <CButton class="ml-2" color="secondary" variant="outline" :disabled="isLoading || isSaving || !hasChanges" @click="resetChanges">
          Poništi
        </CButton>
        <span v-if="hasChanges" class="text-warning ml-3">Promjene nisu spremljene</span>
      </div>

      <CAlert v-if="message" class="mt-3" :color="hasError ? 'danger' : 'success'">
        {{ message }}
      </CAlert>
    </CCardBody>
  </CCard>
</template>

<script>
import { estateAdService, publisherBlacklistService } from "../services/services";

export default {
  name: "PublisherBlacklist",
  data() {
    return {
      publisherOptions: [],
      selectedPublishers: [],
      initialPublishers: [],
      searchText: "",
      manualPublisherName: "",
      isLoading: false,
      isSaving: false,
      message: "",
      hasError: false,
    };
  },
  computed: {
    availablePublishers() {
      const selectedNames = new Set(this.selectedPublishers.map((publisher) => publisher.normalizedName));
      const searchText = this.normalizeName(this.searchText);
      return this.publisherOptions.filter((publisher) => {
        return !selectedNames.has(publisher.normalizedName) && (!searchText || publisher.normalizedName.includes(searchText));
      });
    },
    selectedPublishersSorted() {
      return [...this.selectedPublishers].sort((first, second) => first.name.localeCompare(second.name, "hr"));
    },
    hasChanges() {
      const currentNames = this.selectedPublishers.map((publisher) => publisher.normalizedName).sort();
      const initialNames = this.initialPublishers.map((publisher) => publisher.normalizedName).sort();
      return currentNames.join("|") !== initialNames.join("|");
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    normalizeName(name) {
      return name.trim().toLocaleLowerCase("hr");
    },
    toOption(name, activeCount) {
      return {
        name,
        normalizedName: this.normalizeName(name),
        activeCount: activeCount || 0,
      };
    },
    findPublisher(name) {
      const normalizedName = this.normalizeName(name);
      return this.publisherOptions.find((publisher) => publisher.normalizedName === normalizedName) || this.toOption(name, 0);
    },
    load() {
      this.isLoading = true;
      this.message = "";
      this.hasError = false;
      publisherBlacklistService
        .get()
        .then((response) => {
          const body = response.body;
          this.publisherOptions = body.publishers.map((publisher) => this.toOption(publisher.name, publisher.activeCount));
          this.selectedPublishers = body.blacklistedPublishers.map((name) => this.findPublisher(name));
          this.initialPublishers = this.selectedPublishers.map((publisher) => ({ ...publisher }));
        })
        .catch(() => {
          this.hasError = true;
          this.message = "Blacklistu nije moguće učitati.";
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    addPublisher(publisher) {
      if (!this.selectedPublishers.some((selected) => selected.normalizedName === publisher.normalizedName)) {
        this.selectedPublishers.push(publisher);
      }
      this.message = "";
    },
    addManualPublisher() {
      const name = this.manualPublisherName.trim();
      if (!name) {
        return;
      }
      this.addPublisher(this.findPublisher(name));
      this.manualPublisherName = "";
    },
    removePublisher(publisher) {
      this.selectedPublishers = this.selectedPublishers.filter((selected) => selected.normalizedName !== publisher.normalizedName);
      this.message = "";
    },
    resetChanges() {
      this.selectedPublishers = this.initialPublishers.map((publisher) => ({ ...publisher }));
      this.message = "";
    },
    save() {
      this.isSaving = true;
      this.message = "";
      this.hasError = false;
      publisherBlacklistService
        .replace(this.selectedPublishers.map((publisher) => publisher.name))
        .then(() => {
          this.initialPublishers = this.selectedPublishers.map((publisher) => ({ ...publisher }));
          this.message = "Blacklist agencija je spremljena.";
          estateAdService
            .getAllHoods()
            .then((hoodsResponse) => {
              this.$store.commit("setHoods", hoodsResponse.body);
            })
            .catch(() => {});
        })
        .catch((response) => {
          this.hasError = true;
          this.message = response.body && response.body.message ? response.body.message : "Blacklistu nije moguće spremiti.";
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>

<style scoped>
.publisher-list {
  max-height: 480px;
  overflow-y: auto;
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
}
.publisher-row {
  min-height: 48px;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border-bottom: 1px solid #ebedef;
}
.publisher-row:last-child {
  border-bottom: 0;
}
.publisher-name {
  min-width: 0;
  overflow-wrap: anywhere;
}
.empty-state {
  padding: 1.5rem 0.75rem;
  color: #768192;
  text-align: center;
}
</style>
